import type { Editor } from "@tiptap/vue-3";
import type { AxWidgetStyle, ColorProp, StyleProp } from "../bridge/selection";
import type { CssParagraphAttributes, TextStyleAttributes, TextStyleCssAttributes } from "../rich-text/converter";
import type { AxTextEditorState } from "../store/textEditor";
import type { AxFullTextStyle } from "./attributedString";
import { BulletList } from "@tiptap/extension-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "lucide-vue-next";
import { fromArgb, fromInlineArgb, getArgb } from "../common/utils";
import {
  DEFAULT_TEXT_STYLE,
  getFontSizeFromCss,
  getFontStretchFromCss,
  getFontStyleFromCss,
  getFontWeightFromCss,
  getLetterCasingFromCss,
  getLineSpacingFromCss,
  getListInfoFromBulletList,
  getNumberFromCss,
  getStringAlignment,
} from "./attributedString";

interface Context {
  editor: Editor;
  baseStyle: AxFullTextStyle;
  paragraphAttrs: Partial<CssParagraphAttributes>;
  textStyleAttrs: Partial<TextStyleAttributes>;
}

export function toWidgetStyleFromEditorState(editorState: AxTextEditorState): AxWidgetStyle | undefined {
  const editor = editorState.editor;
  if (!editor) return undefined;

  const baseStyle = editorState.baseStyle ?? DEFAULT_TEXT_STYLE;

  const parAttributes = editor.getAttributes(Paragraph.name) as Partial<CssParagraphAttributes>;
  const textStyle = editor.getAttributes(TextStyle.name) as Partial<TextStyleCssAttributes>;

  const context: Context = {
    editor,
    paragraphAttrs: parAttributes,
    textStyleAttrs: textStyle,
    baseStyle,
  };

  return {
    Family: getTextStyleFontFamilyRunProp(context),
    Size: getTextStyleSizeRunProp(context),
    Weight: { value: getFontWeightFromCss(textStyle.fontWeight) ?? baseStyle.Weight, indeterminate: false },
    Style: { value: getFontStyleFromCss(textStyle.fontStyle) ?? baseStyle.Style, indeterminate: false },
    Stretch: { value: getFontStretchFromCss(textStyle.fontStretch) ?? baseStyle.Stretch, indeterminate: false },
    Color: getTextStyleColorRunProp(context),
    CharacterSpacing: getTextStyleCharacterSpacingRunProp(context),

    HorizontalAlignment: { value: getStringAlignment(parAttributes.textAlign) ?? baseStyle.HorizontalAlignment, indeterminate: false },
    LineSpacing: { value: getLineSpacingFromCss(parAttributes.lineHeight) ?? baseStyle.LineSpacing, indeterminate: false },
    Underline: { value: editor.isActive(Underline.name), indeterminate: false },
    Strikethrough: { value: editor.isActive(Strike.name), indeterminate: false },
    ListInfo: { value: getListInfoFromBulletList(editor.isActive(BulletList.name)), indeterminate: false },
    LetterCasing: { value: getLetterCasingFromCss(textStyle.textTransform), indeterminate: false },
  };
}

function getTextStyleSizeRunProp({ paragraphAttrs, textStyleAttrs, baseStyle }: Context): StyleProp<number> {
  const { fontSize, fontSizeReferenceId, fontSizeReferenceName } = textStyleAttrs;
  const { computedFontSize } = paragraphAttrs;

  if (fontSize) {
    return { value: getFontSizeFromCss(fontSize), indeterminate: false, referenceId: fontSizeReferenceId, referenceName: fontSizeReferenceName };
  }

  if (computedFontSize) {
    return { value: getFontSizeFromCss(computedFontSize), indeterminate: false };
  }

  return baseStyle.Size;
}

function getTextStyleFontFamilyRunProp({ textStyleAttrs, baseStyle }: Context): StyleProp<string> {
  const { fontFamily, fontFamilyReferenceId, fontFamilyReferenceName } = textStyleAttrs;
  if (fontFamily) {
    return { value: fontFamily, indeterminate: false, referenceId: fontFamilyReferenceId, referenceName: fontFamilyReferenceName };
  }

  return baseStyle.Family;
}

function getTextStyleColorRunProp({ textStyleAttrs, baseStyle }: Context): StyleProp<ColorProp> {
  const { color: textColor, colorReferenceId, colorReferenceName } = textStyleAttrs;

  if (textColor) {
    const color = fromInlineArgb(textColor) ?? fromArgb(255, 0, 0, 0);
    const prop: ColorProp = { value: color, referenceId: colorReferenceId, referenceName: colorReferenceName };
    return { value: prop, referenceId: colorReferenceId, referenceName: colorReferenceName, indeterminate: false };
  }

  return { value: baseStyle.Color, indeterminate: false };
}

function getTextStyleCharacterSpacingRunProp({ textStyleAttrs, baseStyle }: Context): StyleProp<number> {
  const { letterSpacing, characterSpaceReferenceId, characterSpaceReferenceName } = textStyleAttrs;

  if (letterSpacing) {
    return { value: getNumberFromCss(letterSpacing), indeterminate: false, referenceId: characterSpaceReferenceId, referenceName: characterSpaceReferenceName };
  }

  return baseStyle.CharacterSpacing;
}
